import React from 'react';

import { Box, Button, Typography, Link, Stack } from '@mui/material';
import { IconWrapper } from 'components/Icon';
import { useAuth } from 'lib/auth';
import { Link as RouterLink } from 'react-router-dom';

import { LAYOUT, VERTICAL_NAV } from '../config';

import { Search } from 'components/Form';

import { getCompanies } from '../../../features/frameworkAgreements/api/getCompanies';
import { useNavbar } from 'lib/navbarContext';
import { paths } from 'routes';
import { ImageAvatar } from 'components/Avatar';
import { NotificationBell } from 'features/notifications/components/NotificationBell';
import { CompanySwitcher } from './CompanySwitcher';

const ItemCard = ({
  Name: name,
  VAT: vatNumber,
  Description: description,
  Type: companyType,
  company_id,
  square_logo_url,
  ...rest
}) => {
  return (
    <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
      <Link
        component={RouterLink}
        to={`/app/${companyType}s/${company_id.toString()}`}
        underline="none"
        color="inherit"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{ img: { borderRadius: 1, width: 20, height: 20 } }}
        >
          <ImageAvatar img={square_logo_url || ''} name={name} searchResult />
          <Typography variant="body1" fontWeight="bold">
            {name} ({vatNumber})
          </Typography>
        </Stack>
      </Link>
    </Stack>
  );
};

export const ContentHeader = ({
  headerSize = 'small',
  superAdmin = false,
  subscription_level = "free",
  showHeader = true,
}) => {
  const { user } = useAuth();
  const { closeMenu } = useNavbar();

  if (!showHeader) {
    return null;
  }

  return (
    <Box
      sx={{
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: 'white',
        minWidth: LAYOUT.width,
        width: {
          xs: `calc(100vw - ${VERTICAL_NAV.closedWidth})`,
          lg: `calc(100vw - ${closeMenu ? VERTICAL_NAV.closedWidth : VERTICAL_NAV.width})`,
        },
        left: {
          xs: VERTICAL_NAV.closedWidth,
          lg: `${closeMenu ? VERTICAL_NAV.closedWidth : VERTICAL_NAV.width}`,
        },
        top: 0,
        flex: 1,
        px: 4,
        minHeight: headerSize === 'small' ? LAYOUT.smallHeaderHeight : LAYOUT.largeHeaderHeight,
        maxHeight: headerSize === 'small' ? LAYOUT.smallHeaderHeight : LAYOUT.largeHeaderHeight,
        position: 'fixed',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'border.main',
      }}
    >
      <Search
        fullWidth
        onChange={() => null}
        queryFunction={(searchTerm) => getCompanies('all', searchTerm, false)}
        ItemCard={(props) => <ItemCard {...props} />}
        optionLabelName="name"
      />

      {!superAdmin && (
        <>
          <NotificationBell />

          <Link
            component={RouterLink}
            to={`/app/${
              user?.Company?.Type
            }s/${user?.Company?.company_id?.toString()}/users`}
            underline="none"
            color="inherit"
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <IconWrapper
                icon="users"
                sx={({ palette }) => ({
                  svg: {
                    color: palette.background.paper,
                    stroke: palette.primary.main,
                    strokeWidth: 2,
                  },
                })}
              />
              <Typography
                variant="button"
                sx={{ fontWeight: 700, fontSize: '0.625rem', lineHeight: 1.5 }}
              >
                Users
              </Typography>
            </Button>
          </Link>

          <Link
            component={RouterLink}
            to={`/app/${
              user?.Company?.Type
            }s/${user?.Company?.company_id?.toString()}`}
            underline="none"
            color="inherit"
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <IconWrapper
                icon="buildingOffice"
                sx={({ palette }) => ({
                  svg: {
                    color: palette.background.paper,
                    stroke: palette.primary.main,
                    strokeWidth: 2,
                  },
                })}
              />
              <Typography
                variant="button"
                sx={{ fontWeight: 700, fontSize: '0.625rem', lineHeight: 1.5 }}
              >
                Company
              </Typography>
            </Button>
          </Link>

          <CompanySwitcher />

          <Link
            component={RouterLink}
            to={`/app/contacts/${user?.user_id?.toString()}`}
            underline="none"
            color="inherit"
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                color: 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <IconWrapper
                icon="user"
                sx={({ palette }) => ({
                  svg: {
                    color: palette.background.paper,
                    stroke: palette.primary.main,
                    strokeWidth: 2,
                  },
                })}
              />
              <Typography
                variant="button"
                sx={{ fontWeight: 700, fontSize: '0.625rem', lineHeight: 1.5 }}
              >
                Profile
              </Typography>
            </Button>
          </Link>
        </>
      )}

      {superAdmin && (
        <Link component={RouterLink} to={paths.settings} underline="none" color="inherit">
          <Button
            variant="contained"
            sx={{
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <IconWrapper
              icon="cog8Tooth"
              sx={({ palette }) => ({
                svg: {
                  color: palette.background.paper,
                  stroke: palette.primary.main,
                  strokeWidth: 2,
                },
              })}
            />
            <Typography
              variant="button"
              sx={{ fontWeight: 700, fontSize: '0.625rem', lineHeight: 1.5 }}
            >
              Settings
            </Typography>
          </Button>
        </Link>
      )}
    </Box>
  );
};
